/**
 * @fileoverview User Limits Context - Focused on permissions and usage limits
 * Part of Phase 2.3 context optimization - split from monolithic UserContext
 */

import { createContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useStore } from '../../store';
import { useUserProfile } from './hooks';
import type { TierLimits, UserUsage } from '../../types/user';

export interface UserLimitsContextType {
    // Limits and usage data
    limits: TierLimits;
    usage: UserUsage;

    // Permission checks
    canCreateStrategy: boolean;
    canSaveBacktest: boolean;
    canShareStrategies: boolean;
    hasAdvancedAnalytics: boolean;
    hasPrioritySupport: boolean;
    hasFullApiAccess: boolean;

    // Built-in strategy access
    hasAccessToBuiltInStrategy: (strategyId: string) => boolean;
    getAccessibleBuiltInStrategies: () => readonly string[] | 'all';
}

export const UserLimitsContext = createContext<UserLimitsContextType | undefined>(undefined);

interface UserLimitsProviderProps {
    children: ReactNode;
}

export const UserLimitsProvider = ({ children }: UserLimitsProviderProps) => {
    const store = useStore();
    const { tier } = useUserProfile();

    // Extract limits and usage from store
    const { limits, usage } = store;

    // Provide safe defaults to avoid undefined errors
    const safeUsage: UserUsage = usage || {
        strategiesCreated: 0,
        savedBacktests: 0
    };

    const safeLimits: TierLimits = limits || {
        maxCustomStrategies: 0,
        maxSavedBacktests: 0,
        canShareStrategies: false,
        accessToBuiltInStrategies: [],
        advancedAnalytics: false,
        prioritySupport: false,
        apiAccess: 'limited'
    };

    // Memoize permission checks to prevent unnecessary recalculations
    const permissions = useMemo(() => ({
        canCreateStrategy: store.canCreateStrategy(),
        canSaveBacktest: store.canSaveBacktest(),
        canShareStrategies: store.canShareStrategies(),
        hasAdvancedAnalytics: safeLimits.advancedAnalytics,
        hasPrioritySupport: safeLimits.prioritySupport,
        hasFullApiAccess: safeLimits.apiAccess === 'full'
    }), [store, safeLimits]);

    // Built-in strategy access functions
    const hasAccessToBuiltInStrategy = useMemo(() =>
        (strategyId: string): boolean => {
            if (safeLimits.accessToBuiltInStrategies === 'all') {
                return true;
            }
            return safeLimits.accessToBuiltInStrategies.includes(strategyId);
        }, [safeLimits.accessToBuiltInStrategies]
    );

    const getAccessibleBuiltInStrategies = useMemo(() =>
        (): readonly string[] | 'all' => safeLimits.accessToBuiltInStrategies,
        [safeLimits.accessToBuiltInStrategies]
    );

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo((): UserLimitsContextType => ({
        limits: safeLimits,
        usage: safeUsage,
        ...permissions,
        hasAccessToBuiltInStrategy,
        getAccessibleBuiltInStrategies
    }), [safeLimits, safeUsage, permissions, hasAccessToBuiltInStrategy, getAccessibleBuiltInStrategies]);

    return (
        <UserLimitsContext.Provider value={contextValue}>
            {children}
        </UserLimitsContext.Provider>
    );
};
